import {
  WSButton,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSGrid,
  WSMessageBox,
  WSSelectOption,
  WSText
} from "@wingspanhq/fe-component-library";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import { PayerEngagementStatus } from "@wingspanhq/wingspan-api-sdk";
import { Nullable } from "primereact/ts-helpers";
import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { queriesPayerRows } from "@query";
import { selectorPayerDisplayName } from "../../../shared/selectors/selectorPayerDisplayName";
import { PAYERS_PATH } from "../../../modules/Payers/paths";
import { routeHelpersWorkLogsPayee } from "../../../modules/WorkLogs/paths";
import { SelectPayerEngagement } from "../../../modules/Payers/components/SelectPayerEngagement";
import { AdditionalEmailsFieldOld } from "../AdditionalEmailsField/AdditionalEmailsFieldOld";
import { useInvoicesFormContext } from "./InvoicesForm";
import { selectorIsPayeeAllowedToInitiateInvoice } from "../../selectors/selectorIsPayeeAllowedToInitiateInvoice";
import { useUserId } from "../../../query/hooks/helpers";

export type ClientSectionProps = WSElementProps;

export type ClientSectionValues = {
  client?: {
    payerId: string | null;
    payerPayeeEngagementId: string | null;
    emailsCC: { email: string }[];
  };
};

export const ClientSection: React.FC<ClientSectionProps> = props => {
  const history = useHistory();
  const { register, setValue, clearErrors, errors, getValues } =
    useFormContext();
  const {
    defaultValues,
    invoicingConfigForPayee,
    payerRow,
    invoice,
    invoiceTemplate
  } = useInvoicesFormContext();

  useEffect(() => {
    if (payerRow) {
      setValue(
        "client.emailsCC",
        (payerRow?.payeeOwnedData?.payerEmailCC || []).map(email => ({ email }))
      );
    }
  }, [payerRow, setValue]);

  const [selectValue, setSelectValue] =
    useState<Nullable<WSSelectOption>>(null);

  const [autoFocus, setAutoFocus] = useState(false);
  const preselectedEmail = defaultValues?.email;

  const queryPreselectedPayerRows =
    queriesPayerRows.searchPayerRows.usePaginatedQuery(
      {
        filter: {
          searchString: preselectedEmail ? { eq: preselectedEmail } : undefined
        }
      },
      { enabled: !!preselectedEmail }
    );

  useEffect(() => {
    if (!preselectedEmail || !queryPreselectedPayerRows.isSuccess) {
      return;
    }

    const payer = (queryPreselectedPayerRows.flatData ?? []).find(
      payer => payer.email === preselectedEmail
    );

    const filteredEngagements = (payer?.engagements ?? []).filter(
      engagement =>
        engagement.status === PayerEngagementStatus.ACTIVATED &&
        engagement.metadata?.duplicate !== "true"
    );

    if (payer && filteredEngagements.length === 1) {
      setAutoFocus(false);
      setValue("client.payerId", payer.id);
      setValue(
        "client.payerPayeeEngagementId",
        filteredEngagements[0].payerEngagementId
      );
    } else {
      setAutoFocus(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedEmail, queryPreselectedPayerRows.isSuccess]);

  useEffect(() => {
    if (!payerRow || !payerRow.user) {
      setSelectValue(null);
      return;
    }

    const payerName = wsName({
      user: payerRow.user,
      member: payerRow.member,
      payeeOwnedData: payerRow.payeeOwnedData
    }).getResolvedName();

    const formValues = getValues() as ClientSectionValues;

    const payerPayeeEngagementId = formValues?.client?.payerPayeeEngagementId;

    const engagement = payerRow.engagements.find(
      engagement => engagement.payerPayeeEngagementId === payerPayeeEngagementId
    );

    setSelectValue({
      value: payerRow.payerId + "/" + payerPayeeEngagementId,
      label: `${payerName} • ${engagement?.engagementName}`,
      labels: [payerRow.user.email],
      avatar: {
        type: "text",
        text: payerName
      }
    });
  }, [getValues, payerRow]);

  const isDisabled = !!invoice || !!invoiceTemplate;

  return (
    <WSElement {...props}>
      <WSText.Heading5 mb="XL">Client assignment</WSText.Heading5>

      <input type="hidden" name="client.memberClientId" ref={register()} />

      <input type="hidden" name="client.payerId" ref={register()} />
      <input
        type="hidden"
        name="client.payerPayeeEngagementId"
        ref={register()}
      />

      <SelectPayerEngagement
        defaultSearchText={preselectedEmail}
        onAddNewPayer={(payer, engagements) => {
          if (engagements.length) {
            const payerEngagement = engagements[0];
            const payerName = selectorPayerDisplayName(payer);

            clearErrors("client");
            setValue("client.payerId", payer.id);
            setValue("client.payerPayeeEngagementId", payerEngagement.id);

            setSelectValue({
              value: payer.id + "/" + payerEngagement.id,
              label: `${payerName} • ${payerEngagement.engagementName}`,
              labels: payer.email ? [payer.email] : [],
              avatar: {
                type: "text",
                text: payerName
              }
            });
          }
        }}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        disabled={isDisabled}
        value={selectValue}
        onChange={newValue => {
          if (newValue) {
            clearErrors("client");
            const [payerId, payerPayeeEngagementId] = newValue.value.split("/");
            setValue("client.payerId", payerId);
            setValue("client.payerPayeeEngagementId", payerPayeeEngagementId);
            setSelectValue(newValue);
          } else {
            setValue("client.payerId", null);
            setValue("client.payerPayeeEngagementId", null);
            setSelectValue(null);
          }
        }}
        status={errors?.client?.payerPayeeEngagementId ? "error" : undefined}
        message={errors?.client?.payerPayeeEngagementId?.message}
      />

      <WSElement colorBackground="gray50" px="M" py="S" mt="M">
        <WSText.ParagraphXs weight="medium" color="gray600" mb="XS">
          Don't see an assignment?
        </WSText.ParagraphXs>
        <WSFlexBox.CenterY wrap="nowrap" justify="space-between">
          <WSText.ParagraphXs color="gray400">
            Some assignments require work logging
          </WSText.ParagraphXs>
          <WSButton
            kind="Link"
            size="S"
            rightIcon="chevron-right"
            onClick={() => history.push(routeHelpersWorkLogsPayee.path)}
          >
            Create work log
          </WSButton>
        </WSFlexBox.CenterY>
        <WSFlexBox.CenterY wrap="nowrap" justify="space-between">
          <WSText.ParagraphXs color="gray400">
            Some assignments are managed by clients directly.
          </WSText.ParagraphXs>
          <WSButton
            kind="Link"
            size="S"
            rightIcon="chevron-right"
            onClick={() => history.push(PAYERS_PATH)}
          >
            View clients
          </WSButton>
        </WSFlexBox.CenterY>
      </WSElement>

      <PreviewPayerEngagement />

      {invoicingConfigForPayee?.additionalGuidelines.enabled &&
      invoicingConfigForPayee?.additionalGuidelines?.value ? (
        <WSMessageBox.Info
          data-testid="additionalGuidelines"
          my="M"
          noBorder
          // title={`Guidelines from Client (${memberClient?.name || "-"}):`}
        >
          <WSText.ParagraphSm color="gray500" weight="book">
            {invoicingConfigForPayee?.additionalGuidelines.value}
          </WSText.ParagraphSm>
        </WSMessageBox.Info>
      ) : null}
    </WSElement>
  );
};

const PreviewPayerEngagement: React.FC = () => {
  const { payerRow, invoicingConfigForPayee } = useInvoicesFormContext();
  const { watch } = useFormContext();
  const userId = useUserId();

  const payerPayeeEngagementId = watch("client.payerPayeeEngagementId");

  const engagement = useMemo(
    () =>
      payerRow?.engagements.find(
        engagement =>
          engagement.payerPayeeEngagementId === payerPayeeEngagementId
      ),
    [payerPayeeEngagementId, payerRow?.engagements]
  );

  const isPayeeInitiatedInvoicesNotAllowed =
    !selectorIsPayeeAllowedToInitiateInvoice(invoicingConfigForPayee, userId);

  return (
    <>
      {payerRow ? (
        <WSGrid mt="M" mb="L" gutter="M">
          <WSGrid.Item span={{ m: "6" }}>
            <WSText.ParagraphSm mb="S" color="gray500">
              Contact name
            </WSText.ParagraphSm>
            <WSText.ParagraphSm>
              {payerRow.payeeOwnedData?.payerName || "–"}
            </WSText.ParagraphSm>
          </WSGrid.Item>
          <WSGrid.Item span={{ m: "6" }}>
            <WSText.ParagraphSm mb="S" color="gray500">
              Client company
            </WSText.ParagraphSm>
            <WSText.ParagraphSm>
              {payerRow.payeeOwnedData?.payerCompanyName || "–"}
            </WSText.ParagraphSm>
          </WSGrid.Item>
          <WSGrid.Item span={{ m: "6" }}>
            <WSText.ParagraphSm mb="S" color="gray500">
              Contact email
            </WSText.ParagraphSm>
            <WSText.ParagraphSm>{payerRow.user?.email}</WSText.ParagraphSm>
          </WSGrid.Item>
          <WSGrid.Item span={{ m: "6" }}>
            <WSText.ParagraphSm mb="S" color="gray500">
              Assignment name
            </WSText.ParagraphSm>
            <WSText.ParagraphSm>
              {engagement?.engagementName || "–"}
            </WSText.ParagraphSm>
          </WSGrid.Item>
        </WSGrid>
      ) : null}

      <AdditionalEmailsFieldOld
        hidden={!payerRow}
        name="client.emailsCC"
        inputLabel="Contact email CC"
        buttonLabel="Add email recipient"
        buttonDescription="Additional email addresses will be cc’d"
        disabled={isPayeeInitiatedInvoicesNotAllowed}
      />
    </>
  );
};
